/**
 * Dependencies
 */
import http from '../../../services/HttpService'

/**
 * Return a revised value set
 *
 * @param  {Array} arr
 * @param  {Number} total
 * @return {Array}
 */
const transformValues = (arr, total) => {
    let values = new Array(total)
    for (let i = 0; i < total; i++) {
        values[i] = arr[i] ? arr[i] : null
    }
    if (total > 2) {
        values.splice(total - 1, 0, '...')
    }
    return values
}

/**
 * Return a revised input dataset
 *
 * @param  {Array} arr
 * @param  {Number} total
 * @return {Object}
 */
const transformInputData = (arr, total) => arr.map(item => {
    return {
        file_category: item.file_category,
        is_total: item.is_total,
        label: item.label,
        lucro_category: item.lucro_category,
        values: transformValues(item.values, total),
        data: transformInputData(item.data, total)
    }
})

/**
 * Transform the headers to the appropriate format
 *
 * @param  {Array} headers
 * @param  {Number} total
 * @return {Array}
 */
const transformInputHeaders = (headers, total) => {
    headers.splice(0, 0, 'Lucro Category')
    if (total > 2) {
        headers.splice(total, 0, '...')
    }
    return headers
}

/**
 * Convert the payload to a useable format
 *
 * @param  {Object} payload
 * @return {Object}
 */
const convertPayload = payload => {
    payload.output.summary.opex = payload.output.summary.opex.map((item, idx) => {
        item.id = idx
        return item
    })
    payload.output.summary.income = payload.output.summary.income.map((item, idx) => {
        item.id = idx
        return item
    })
    payload.output.summary.opex_category_list.push('Excluded Items')
    payload.output.summary.opex_category_list = payload.output.summary.opex_category_list.filter(item => item !== null)
    payload.output.summary.income_category_list = payload.output.summary.income_category_list.filter(item => item !== null)
    payload.input = {
        meta: payload.input.meta,
        data: transformInputData(payload.input.data.data, payload.input.meta.index.length)
    }
    payload.input.meta.index = transformInputHeaders(payload.input.meta.index, payload.input.meta.index.length)

    return payload
}

/**
 * Upload a given file to the server and handle the mapping payload
 *
 * @param  {Function} options.commit
 * @param  {File} data
 * @return {Void}
 */
const uploadFile = ({ commit, dispatch }, data) => {
    commit('SET_LOADING')
    http.post('data-import/import-files/pnl/', data, {
        headers: {'Content-Type': 'multipart/form-data'}
    })
    .then(res => {
        commit('SET_DATA', res.data)
        commit('projects/form/UPDATE_FILE_ID', {
            id: res.data.meta.pnl_file_id,
            type: 'pnl_file_id'
        }, { root: true })
        commit('RESET_LOADING')
    })
    .catch(e => {
        if (e.response) { commit('SET_DATA_ERROR', e.response.data) }
        dispatch('addNotification', {
            title: 'Error',
            type: 'error',
            text: 'There was a problem uploading your file.',
            timeout: true
        }, { root: true })
        commit('RESET_LOADING')
    })
}

/**
 * Update the selected category
 *
 * @param  {Function} options.commit
 * @param  {String} category
 * @return {Void}
 */
const updateSelectedCategory = ({ commit }, payload) => {
    commit('UPDATE_SEARCH_TERM', null)
    commit('UPDATE_SELECTED_CATEGORY', payload)
}

/**
 * Update an item's category
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const updateItemCategory = ({ commit }, payload) => {
    commit('UPDATE_ITEM_CATEGORY', payload)
}

/**
 * Reset the state
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const resetState = ({ commit }) => {
    commit('RESET_STATE')
}

/**
 * Update the importer active tab
 *
 * @param  {Object} options.commit
 * @param  {String} type
 * @return {Void}
 */
const updateTab = ({ commit }, type) => {
    commit('UPDATE_TAB', type)
}

/**
 * Update the search term
 *
 * @param  {Function} options.commit
 * @param  {String} term
 * @return {Void}
 */
const updateSearchTerm = ({ commit }, term) => {
    commit('UPDATE_SEARCH_TERM', term)
}

/**
 * Import the reviewed OPEX line items
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const importItems = ({ commit, state, dispatch }) => {
    commit('SET_LOADING')
    http.patch(`data-import/import-files/${state.data.meta.pnl_file_id}/`, {
        opex: state.data.opex,
        income: state.data.income
    })
    .then(res => {
        dispatch('projects/form/ingestOpexValues', res.data, {
            root: true
        })
        commit('RESET_LOADING')
        commit('IMPORT_SUCCESSFUL')
    })
    .catch(() => {
        commit('RESET_LOADING')
        commit('IMPORT_FAILED')
    })
}

/**
 * Allow the user to request assistance with a file that fails to upload properly
 *
 * @param  {Function} options.commit
 * @param  {Number} id
 * @return {Promise}
 */
const requestFileAssistance = ({ commit, state }) => {
    commit('REQUEST_FILE_ASSISTANCE')
    http.post(`data-import/import-files/${state.errors.opex_file_id}/support/`)
        .then(res => commit('SUPPORT_REQUEST_SUCCESS', res.data))
        .catch(err => commit('SUPPORT_REQUEST_FAILURE', err.response.data))
}

/**
 * Toggle the input tree table
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const toggleInputBindings = ({ commit }) => commit('TOGGLE_INPUT_BINDINGS')

/**
 * Update an item's input category
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const changeCategory = ({ state, commit }, payload) => {
    http.patch(`data-import/import-files/${state.data.meta.pnl_file_id}/`, {
        input: [{
            label: payload.item.label,
            lucro_category: payload.value,
            file_category: payload.item.file_category
        }]
    })
    .then(res => commit('SET_DATA', convertPayload(res.data)))
    .catch(err => console.log(err))
}

/**
 * Update the category for an existing output item
 *
 * @param  {Object} options.state
 * @param  {Object} payload
 * @return {Void}
 */
const updateCategory = ({ commit, state, dispatch }, payload) => {
    http.patch(`data-import/import-files/${state.data.meta.pnl_file_id}/update/`, payload)
        .then(res => {
            commit('SET_DATA', res.data)
            dispatch('addNotification', {
                title: 'Item Reassigned',
                type: 'success',
                text: 'The line item was successfully recategorized',
                timeout: true
            }, { root: true })
        })
        .catch(e => {
            if (e.response) { commit('SET_DATA_ERROR', e.response.data) }
            dispatch('addNotification', {
                title: 'Error',
                type: 'error',
                text: 'There was a problem uploading your file.',
                timeout: true
            }, { root: true })
        })
}

const importData = ({ state, dispatch, commit }) => {
    commit('SET_LOADING')
    http.get(`data-import/import-files/${state.data.meta.pnl_file_id}/import/`)
    .then(res => {
        dispatch('projects/form/ingestOpexValues', res.data, {
            root: true
        })
        commit('RESET_LOADING')
        commit('IMPORT_SUCCESSFUL')
    })
    .catch(() => {
        commit('RESET_LOADING')
        commit('IMPORT_FAILED')
    })
}

export default {
    updateTab,
    importData,
    resetState,
    uploadFile,
    importItems,
    updateCategory,
    changeCategory,
    updateSearchTerm,
    updateItemCategory,
    requestFileAssistance,
    updateSelectedCategory,
    toggleInputBindings
}



// WEBPACK FOOTER //
// ./src/state/modules/pnl-importer/actions.js